import { sum } from 'utils/calculator';
import { calcSegmentPointsTotal } from 'utils/functions';
import { ER_OFFLINE_FEE, OFFLINE_FEE } from 'types/fees';
import { partition, propEq } from 'ramda';
import { OUTBOUND } from 'constants/booking';
import serviceTypeCodes from 'constants/serviceTypeCodes';
import { marginTariff } from 'constants/tariffCodes';
import { productFamilyCodes } from 'types/productFamilyCodes';

export const trainMarginCodes = [marginTariff.MARGIN_TRAIN, marginTariff.MARGIN_TRAIN_ER];
export const splitLegsByDirection = partition(propEq('direction', OUTBOUND));

export const getHotels = (hotels) => hotels?.filter((hotel) => !hotel.cancelled);
export const hasHotels = (hotels) => getHotels(hotels)?.length > 0;
export const getHotelPrices = (hotels) => {
  const nonCancelledHotels = getHotels(hotels);
  const hotelsPrices = nonCancelledHotels?.map((hotel) => hotel.price);

  return sum(hotelsPrices);
};
export const getHotelMargins = (hotels) => {
  const nonCancelledHotels = getHotels(hotels);
  const hotelsMargins = nonCancelledHotels?.map((hotel) => hotel.margin);

  return sum(hotelsMargins);
};

export const getOfflineFees = (fees) =>
  fees?.filter(({ code, cancelled }) => (code === OFFLINE_FEE || code === ER_OFFLINE_FEE) && !cancelled);

export const getTotalPoints = (booking) => {
  const outboundPoints = calcSegmentPointsTotal(booking?.outbound);
  const inboundPoints = calcSegmentPointsTotal(booking?.inbound);

  return sum([outboundPoints, inboundPoints]);
};

export const calcSegmentPriceTotal = (segments) => {
  const getProductsPrices = segments?.reduce((acc, segment) => {
    const price = segment?.products?.map((product) => product?.price) || [0];

    return sum([acc, ...price]);
  }, 0);

  const getExtraProductsPrices = segments?.reduce((acc, segment) => {
    const priceExtra = segment?.extraProducts
      ?.filter((extra) => !trainMarginCodes.includes(extra.code))
      ?.map((extraProduct) => extraProduct?.price) || [0];

    return sum([acc, ...priceExtra]);
  }, 0);

  return sum([getProductsPrices, getExtraProductsPrices]);
};

export const getTotalPrice = (booking) => {
  const outboundPrice = calcSegmentPriceTotal(booking?.outbound);
  const inboundPrice = calcSegmentPriceTotal(booking?.inbound);

  return sum([outboundPrice, inboundPrice]);
};

export const mergePassengersByType = (bookingItems) => [...new Map(bookingItems.map((item) => [item.passenger.type, item])).values()];

export const hasOnlyERLegs = (legs) => legs.every((l) => l.serviceType?.code === serviceTypeCodes.GREENSPEED);

export const isBookingMultiJourney = (booking) =>
  !!booking?.additionalDetails?.find((ad) => ad?.key === 'multi_journey' && ad?.value === 'true');

export const getMultiJourneyLegs = (booking) => {
  let multiJourneyLegs = [];

  let journeys = booking.additionalDetails.filter((ad) => ad.key.includes('segment_') && ad.value.includes('journey_'));

  for (let journeyDetail of journeys) {
    let journeyId = journeyDetail.value.split('_')[1];
    let journey = multiJourneyLegs.find((l) => l.journeyId === journeyId);

    if (!journey) {
      journey = {
        journeyId: journeyId,
        legs: []
      };

      multiJourneyLegs.push(journey);
    }

    journey.legs.push(...booking.outbound.concat(booking.inbound).filter((l) => l.id === journeyDetail.key));
  }

  return multiJourneyLegs;
};

export const isBookingMiceGroup = ({ details = [] }) =>
  !!details.find(({ key, value }) => key === productFamilyCodes.MICEGROUP && value === 'yes');
